import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Mail, ArrowUpRight, Users, Volume2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../i18n/translations';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Liste complète des sports avec avantages audio - tous disponibles
const sportsData = [
  {
    id: 'rugby',
    name: 'Rugby',
    advantage: 'Optimisez les appels défensifs, la communication en touche et les annonces de combinaisons dans le chaos du match.',
    players: '15-23 joueurs'
  },
  {
    id: 'football',
    name: 'Football',
    advantage: 'Coordonnez le pressing collectif et les replacements défensifs grâce à une communication audio instantanée.',
    players: '11-25 joueurs'
  },
  {
    id: 'basketball',
    name: 'Basketball',
    advantage: 'Anticipez les switches défensifs et les pick & roll avec des appels clairs et rapides.',
    players: '5-12 joueurs'
  },
  {
    id: 'handball',
    name: 'Handball',
    advantage: 'Synchronisez les rotations offensives et les couvertures défensives en 3-5 secondes d\'action.',
    players: '7-14 joueurs'
  },
  {
    id: 'volleyball',
    name: 'Volleyball',
    advantage: 'Améliorez la répartition des zones et les appels de balle dans l\'environnement bruyant des salles.',
    players: '6-12 joueurs'
  },
  {
    id: 'hockey_glace',
    name: 'Hockey sur Glace',
    advantage: 'Communiquez efficacement malgré les 110dB de bruit ambiant et la vitesse du jeu.',
    players: '6-20 joueurs'
  },
  {
    id: 'hockey_gazon',
    name: 'Hockey sur Gazon',
    advantage: 'Coordonnez les phases de corners et les transitions rapides sur grand terrain.',
    players: '11-16 joueurs'
  },
  {
    id: 'water_polo',
    name: 'Water-Polo',
    advantage: 'Surmontez la barrière acoustique de l\'eau pour des appels tactiques précis.',
    players: '7-13 joueurs'
  },
  {
    id: 'futsal',
    name: 'Futsal',
    advantage: 'Gérez les rotations et les pressing dans l\'espace réduit avec une communication ultra-rapide.',
    players: '5-10 joueurs'
  },
  {
    id: 'lacrosse',
    name: 'Lacrosse',
    advantage: 'Synchronisez les phases offensives et les slides défensifs sur un terrain de grande taille.',
    players: '10-20 joueurs'
  },
  {
    id: 'football_americain',
    name: 'Football Américain',
    advantage: 'Optimisez les audibles et les ajustements pré-snap avec une reconnaissance vocale précise.',
    players: '11-53 joueurs'
  },
  {
    id: 'cricket',
    name: 'Cricket',
    advantage: 'Coordonnez les placements défensifs et les stratégies de bowling en temps réel.',
    players: '11-15 joueurs'
  },
  {
    id: 'ultimate',
    name: 'Ultimate Frisbee',
    advantage: 'Améliorez les appels de cut et les couvertures défensives sur grand terrain sans arbitre.',
    players: '7-14 joueurs'
  },
  {
    id: 'roller_hockey',
    name: 'Roller Hockey',
    advantage: 'Communiquez dans l\'environnement sonore difficile des patinoires indoor.',
    players: '5-10 joueurs'
  },
  {
    id: 'netball',
    name: 'Netball',
    advantage: 'Optimisez les transitions et les appels dans les zones restreintes de jeu.',
    players: '7-12 joueurs'
  }
];

const Sports = () => {
  const { language } = useLanguage();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSport, setSelectedSport] = useState(null);
  const [email, setEmail] = useState('');
  const [clubName, setClubName] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, { 
        sport: selectedSport?.id, 
        email, 
        club_name: clubName || null 
      });
      setSuccess(true);
      setEmail('');
      setClubName('');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (sport) => {
    setSelectedSport(sport);
    setSuccess(false);
    setDialogOpen(true);
  };

  return (
    <div data-testid="sports-page" className="bg-background">
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center hero-gradient noise-overlay overflow-hidden" data-testid="sports-hero">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
        
        <div className="container-asp relative z-10 py-32">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="max-w-3xl mx-auto text-center"
          >
            <span className="text-secondary text-sm font-medium tracking-wider uppercase">
              {t('sports.subtitle', language)}
            </span>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mt-4 mb-6">
              {t('sports.title', language)}<br />
              <span className="text-gradient">{t('sports.title2', language)}</span>
            </h1>
            <p className="text-xl text-white/60 leading-relaxed max-w-2xl mx-auto">
              {t('sports.desc', language)}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Liste de tous les sports */}
      <section className="section-padding bg-background" data-testid="all-sports">
        <div className="container-asp">
          <motion.div 
            initial={{ opacity: 0 }} 
            whileInView={{ opacity: 1 }} 
            viewport={{ once: true }} 
            className="text-center mb-16"
          >
            <span className="text-primary text-sm font-medium tracking-wider uppercase">
              {t('sports.chooseSubtitle', language)}
            </span>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mt-4 mb-6">
              {t('sports.chooseTitle', language)}
            </h2>
            <p className="text-white/50 max-w-3xl mx-auto mb-4">
              {t('sports.chooseDesc', language)}
            </p>
            <p className="text-white/40 max-w-2xl mx-auto text-sm">
              {t('sports.chooseDesc2', language)}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sportsData.map((sport, index) => (
              <motion.div 
                key={sport.id}
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                viewport={{ once: true }} 
                transition={{ delay: index * 0.03 }}
              >
                <Card className="card-glass h-full p-6 hover:border-primary/30 transition-all group">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-xl font-semibold text-white">{sport.name}</h3>
                    <div className="flex items-center gap-2 text-white/30 text-xs">
                      <Users className="w-3 h-3" />
                      <span>{sport.players}</span>
                    </div>
                  </div>
                  <p className="text-white/50 text-sm leading-relaxed mb-6">
                    {sport.advantage}
                  </p>
                  <Button 
                    onClick={() => openDialog(sport)}
                    className="w-full btn-primary"
                    data-testid={`waitlist-btn-${sport.id}`}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {t('sports.joinWaitlist', language)}
                  </Button>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Dialog d'inscription */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl">
              Liste d'attente — {selectedSport?.name}
            </DialogTitle>
            <DialogDescription className="text-white/50">
              Soyez informé dès que la solution sera disponible pour {selectedSport?.name}.
            </DialogDescription>
          </DialogHeader>
          
          {success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Inscription confirmée</h3>
              <p className="text-white/50">
                Vous serez contacté dès que {selectedSport?.name} sera disponible.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="text-sm text-white/50 mb-2 block">Email *</label>
                <Input 
                  type="email" 
                  required 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  placeholder="votre@email.com"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="waitlist-email-input"
                />
              </div>
              <div>
                <label className="text-sm text-white/50 mb-2 block">Nom du club (optionnel)</label>
                <Input 
                  value={clubName} 
                  onChange={(e) => setClubName(e.target.value)} 
                  placeholder="Votre club"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="waitlist-club-input"
                />
              </div>
              
              <div className="bg-white/5 rounded-lg p-4 mt-4">
                <div className="flex items-start gap-3">
                  <Volume2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-white mb-1">Avantage audio</div>
                    <p className="text-white/50 text-sm">{selectedSport?.advantage}</p>
                  </div>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full btn-secondary py-4 mt-4" 
                disabled={loading}
                data-testid="waitlist-submit-btn"
              >
                {loading ? 'Inscription...' : "Rejoindre la liste d'attente"}
              </Button>
              <p className="text-white/30 text-xs text-center">
                Pas de spam • Données confidentielles
              </p>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Stats section */}
      <section className="py-16 bg-gradient-to-b from-background to-black/50 border-y border-white/5">
        <div className="container-asp">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-gradient mb-2">15</div>
              <div className="text-white/50 text-sm">{t('sports.available', language)}</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-gradient mb-2">3D</div>
              <div className="text-white/50 text-sm">{t('sports.spatial3d', language)}</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-gradient mb-2">100%</div>
              <div className="text-white/50 text-sm">{t('sports.customizable', language)}</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-gradient mb-2">24h</div>
              <div className="text-white/50 text-sm">{t('sports.response24h', language)}</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-black/50" data-testid="sports-cta">
        <div className="container-asp text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              {t('sports.notListed', language)}
            </h2>
            <p className="text-white/60 text-lg mb-8 max-w-xl mx-auto">
              {t('sports.notListedDesc', language)}
            </p>
            <Link to="/contact">
              <Button className="btn-secondary text-base px-10 py-4 group" data-testid="sports-contact-btn">
                {t('sports.contactUs', language)}
                <ArrowUpRight className="ml-2 w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Sports;
